import React, { useState } from "react";
import { toast } from "react-toastify";
import { thirteenWeekCashFlowApi } from "../../helpers/thirteenWeekCashFlowApi";
import { optimisticOverride } from "../../slices/finance/thirteenWeekCashFlowSlice";
import KpiCard from "./KpiCard";
import AlertRow from "./AlertRow";
import Chip from "./Chip";
import { dateLabel, dayLabel, money, isWeekendOrHoliday } from "./money";
import CurrencyInput from "./CurrencyInput";

function sourceLabel(source) {
  if (source === "cleared_today") return "Cleared today";
  if (source === "revenue_trailing_actual") return "Trailing actual";
  if (source === "revenue_unapproved_fallback") return "Unapproved fallback";
  if (source?.startsWith("budget:")) return "Budget";
  if (source === "forecast_planning") return "Forecast Planning";
  if (source === "revenue_lookback") return "Unapproved fallback";
  return source;
}

function CardOverrideEditor({ row, source, onSave, onReset }) {
  const [amount, setAmount] = useState(source.amount ?? "");
  const [note, setNote] = useState(source.meta?.note || "");
  const currentNote = source.meta?.note || "";
  const saveIfChanged = (nextAmount = amount, nextNote = note) => {
    const amountChanged = Number(nextAmount) !== Number(source.amount);
    const noteChanged = nextNote !== currentNote;
    if (!amountChanged && !noteChanged) return;
    onSave(row, "credit_card", nextAmount, nextNote, source.meta.cardId);
  };
  return (
    <div className="grid items-end gap-2 md:grid-cols-[1fr_8rem_1fr_auto]">
      <span className="text-xs text-gray-600">
        {source.meta.cardName || source.label || "Credit card"}{" "}
        {source.meta.mask ? `···${source.meta.mask}` : ""}
      </span>
      <CurrencyInput
        value={amount}
        onChange={setAmount}
        onBlur={() => saveIfChanged()}
        className="h-8 rounded border-gray-200 text-right text-xs"
      />
      <input
        type="text"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        onBlur={() => saveIfChanged()}
        placeholder="Note"
        className="h-8 rounded border-gray-200 text-xs"
      />
      <button
        type="button"
        className="h-8 text-xs text-gray-500"
        onClick={() => source.meta.override && onReset(row, "credit_card", source.meta.cardId)}
      >
        Reset
      </button>
    </div>
  );
}

const categories = [
  ["operating_revenue", "Operating revenue", "text-emerald-700"],
  ["other_inflow", "Other cash in", "text-emerald-700"],
  ["sales_tax", "Sales tax", "text-red-700"],
  ["ap_operating", "AP – Operating", "text-red-700"],
  ["ap_inventory", "AP – Inventory", "text-red-700"],
  ["credit_card", "Credit card", "text-red-700"],
  ["payroll", "Payroll", "text-red-700"],
  ["owner_distribution", "Owner distributions", "text-red-700"],
  ["debt", "Debt repay", "text-red-700"],
  ["other_outflow", "Other cash out", "text-red-700"],
];

const addDateKey = (value, days) => {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const formatDateKey = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return `${month}/${day}/${year}`;
};

function PriorWeekForecast({ history, weekStart, loading, error, hiddenCategories }) {
  const priorWeek = history?.priorWeeks?.find((item) => item.weekStart === weekStart);
  const actualWeek = priorWeek?.actual;
  const forecastWeek = priorWeek?.week;
  const actualDays = history?.daily || [];
  const savedDays = priorWeek?.daily || [];
  const dates = Array.from({ length: 7 }, (_, index) => addDateKey(weekStart, index));
  const actualByDate = new Map(actualDays.map((row) => [row.date, row]));
  const savedByDate = new Map(savedDays.map((row) => [row.date, row]));
  const savedHeaderDate = formatDateKey(
    String(priorWeek?.generatedAt || priorWeek?.sourceWeekStart || "").slice(0, 10),
  );
  const header = loading
    ? "Loading prior-week forecast…"
    : forecastWeek
      ? `Forecast saved ${savedHeaderDate} for the week of ${formatDateKey(weekStart)} (offset ${priorWeek.offsetWeeks || 0})`
      : `Forecast unavailable: ${priorWeek?.unavailableReason || "No published forecast is available for this week."}`;
  const rows = [
    { key: "beginningCash", label: "Beginning cash" },
    ...categories
      .filter(([key]) => !hiddenCategories.has(key))
      .map(([key, label]) => ({ key, label })),
    { key: "endingCash", label: "Ending cash" },
  ];
  const weeklyValue = (week, key) => (
    key === "beginningCash" || key === "endingCash"
      ? week?.[key]
      : week?.values?.[key]
  );
  const dailyValue = (day, key) => (
    key === "beginningCash" || key === "endingCash"
      ? day?.[key]
      : day?.values?.[key]
  );
  const savedDailyValue = (index, key) => {
    const day = savedByDate.get(dates[index]);
    if (!day) return undefined;
    if (key === "endingCash") return day?.endingCash;
    if (key === "beginningCash") {
      return index === 0
        ? forecastWeek?.beginningCash
        : savedByDate.get(dates[index - 1])?.endingCash;
    }
    return day.values?.[key] ?? 0;
  };
  const format = (value) => (value == null ? "—" : money(value));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-gray-900">Daily ledger · Week of {formatDateKey(weekStart)}</p>
          <p className="text-xs text-gray-500">{header}</p>
          {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
        </div>
        <p className="text-xs text-gray-500">Past ending cash is rebuilt from today's bank balance less categorized bank activity.</p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-[1400px] w-full text-sm">
          <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            <tr>
              <th className="px-3 py-2 text-left">Daily ledger</th>
              {dates.map((date) => <th key={date} className="px-3 py-2 text-right">{dayLabel(date)}<span className="mt-1 block normal-case">Actual / saved forecast</span></th>)}
              <th className="px-3 py-2 text-right">Week actual</th>
              <th className="px-3 py-2 text-right">Forecast</th>
              <th className="px-3 py-2 text-right">Variance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, label }) => {
              const actual = weeklyValue(actualWeek, key);
              const forecast = weeklyValue(forecastWeek, key);
              return (
                <tr key={key} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium text-gray-700">{label}</td>
                  {dates.map((date, index) => {
                    const actualValue = dailyValue(actualByDate.get(date), key);
                    const savedValue = savedDailyValue(index, key);
                    return (
                      <td key={date} className="px-3 py-2 text-right tabular-nums">
                        <span className="block">{format(actualValue)}</span>
                        {savedValue != null && <span className="block text-[10px] text-gray-400">Fcst {format(savedValue)}</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right tabular-nums">{format(actual)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{format(forecast)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{actual == null || forecast == null ? "—" : money(actual - forecast)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {forecastWeek && (!priorWeek?.daily || priorWeek.daily.length === 0) && (
        <p className="text-xs text-gray-500">Daily forecast detail starts with newly saved forecasts.</p>
      )}
    </div>
  );
}

export default function DailyForecastTab({
  summary,
  holidays = [],
  loading,
  error,
  dispatch,
  settings,
  onRefresh,
  history,
  historyLoading,
  historyError,
  onHistoryRequested,
}) {
  const [start, setStart] = useState(0);
  const [historyWeek, setHistoryWeek] = useState(0);
  const [editing, setEditing] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [detailAmount, setDetailAmount] = useState("");
  const [detailNote, setDetailNote] = useState("");
  const [entryForm, setEntryForm] = useState({ label: "", amount: "" });
  const selectedRow = selectedCell
    ? summary?.daily?.find((item) => item.date === selectedCell.row.date) ||
      selectedCell.row
    : null;
  const holidayDates = holidays.length ? holidays : summary?.holidays || [];
  const days = summary?.daily || [];
  const hiddenCategories = new Set(
    settings?.settings?.display?.hiddenCategories ||
      settings?.display?.hiddenCategories ||
      [],
  );
  const visible = days.slice(start, start + 14);
  const overrides = days.filter((row) => row.override).length;
  const priorWeekOptions = summary?.window?.start ? Array.from({ length: 8 }, (_, index) => {
    const weeksBack = index + 1;
    return {
      value: -weeksBack,
      label: `Week of ${formatDateKey(addDateKey(summary?.window?.start, -7 * weeksBack))}`,
    };
  }) : [];
  const selectHistoryWeek = (nextWeek) => {
    setHistoryWeek(nextWeek);
    if (nextWeek < 0) onHistoryRequested?.();
  };
  const save = async (row, category, value, note = null, sourceKey = "") => {
    setEditing(null);
    if (value === "" || Number.isNaN(Number(value))) return;
    const payload = {
      date: row.date,
      category,
      amount: Number(value),
      ...(sourceKey ? { sourceKey } : {}),
      ...(note == null ? {} : { note }),
    };
    dispatch(optimisticOverride(payload));
    try {
      await thirteenWeekCashFlowApi.saveOverride(payload);
      toast.success("Forecast override saved", { position: "bottom-right" });
      onRefresh?.();
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.error || "Override could not be saved",
        { position: "bottom-right" },
      );
    }
  };
  const clearOverride = async (row, category, sourceKey = "") => {
    try {
      await thirteenWeekCashFlowApi.deleteOverride({
        date: row.date,
        category,
        ...(sourceKey ? { sourceKey } : {}),
      });
      toast.success("Forecast override cleared", { position: "bottom-right" });
      if (selectedCell?.row.date === row.date && selectedCell?.category === category) {
        setDetailAmount("");
        setDetailNote("");
      }
      onRefresh?.();
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.error || "Override could not be cleared",
        { position: "bottom-right" },
      );
    }
  };
  const openDetail = (row, category) => {
    const override = row.overrides?.[category];
    setSelectedCell({ row, category });
    setDetailAmount(row.values?.[category] ?? "");
    setDetailNote(override?.note || "");
    setEntryForm({ label: "", amount: "" });
  };
  const saveDetail = async () => {
    if (
      !selectedCell ||
      !selectedRow ||
      selectedRow.date < summary.window.today
    )
      return;
    await save(selectedRow, selectedCell.category, detailAmount, detailNote);
  };
  const addEntry = async () => {
    if (
      !selectedCell ||
      !selectedRow ||
      !entryForm.label.trim() ||
      entryForm.amount === ""
    )
      return;
    try {
      await thirteenWeekCashFlowApi.createSchedule({
        category: selectedCell.category,
        label: entryForm.label.trim(),
        amount: Number(entryForm.amount),
        frequency: "once",
        anchorDate: selectedRow.date,
        businessDayRule: "none",
      });
      toast.success("Cash entry added", { position: "bottom-right" });
      setEntryForm({ label: "", amount: "" });
      onRefresh?.();
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.error || "Cash entry could not be added",
        { position: "bottom-right" },
      );
    }
  };
  if (loading)
    return (
      <div className="flex min-h-[420px] items-center justify-center text-sm text-gray-500">
        Loading daily forecast…
      </div>
    );
  if (error)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
        {error}
      </div>
    );
  if (!summary)
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-600">
        No daily forecast is available.
      </div>
    );
  const unapprovedRevenueWarnings = (summary.alerts || []).filter(
    (alert) => alert.meta?.unapproved === true,
  );
  const warningRows = unapprovedRevenueWarnings.map((alert) => (
    <AlertRow
      key={`${alert.code}-${alert.meta?.month || alert.message}`}
      alerts={[alert]}
    />
  ));
  if (historyWeek < 0) {
    const weekStart = addDateKey(summary.window.start, 7 * historyWeek);
    return (
      <div className="space-y-4">
        {warningRows}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            View
            <select
              value={historyWeek}
              onChange={(event) => selectHistoryWeek(Number(event.target.value))}
              className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              <option value={0}>Current forecast</option>
              {priorWeekOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>
        <PriorWeekForecast
          history={history}
          weekStart={weekStart}
          loading={historyLoading}
          error={historyError}
          hiddenCategories={hiddenCategories}
        />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {warningRows}
      <div className="grid grid-cols-2 rounded-lg border border-gray-200 bg-white sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label="Opening cash"
          value={summary.openingCash?.amount}
          caption={
            summary.metadata?.openingCashSourceLabel ||
            (summary.openingCash?.source === "effective_capital"
              ? "Effective Capital · Kings Loot cash"
              : "Plaid depository accounts")
          }
          moneyValue
        />
        <KpiCard
          label="91-day ending cash"
          value={summary.kpis?.finalWeekEndingCash}
          moneyValue
        />
        <KpiCard
          label="Minimum daily cash"
          value={summary.kpis?.minProjectedCash}
          caption={dateLabel(summary.kpis?.minDate)}
          moneyValue
          tone="text-orange-700"
        />
        <KpiCard
          label="Days below floor"
          value={summary.kpis?.daysBelowFloor ?? "—"}
        />
        <KpiCard
          label="Next scheduled outflow"
          value={
            summary.kpis?.nextScheduledOutflow
              ? dateLabel(summary.kpis.nextScheduledOutflow.date)
              : "—"
          }
          caption={
            summary.kpis?.nextScheduledOutflow
              ? money(summary.kpis.nextScheduledOutflow.amount)
              : null
          }
        />
        <KpiCard label="Overrides" value={overrides} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-lg font-semibold text-gray-900">Daily ledger</p>
          <p className="text-xs text-gray-500">
            Select any value to inspect sources and edit future dates.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
            View
            <select
              value={historyWeek}
              onChange={(event) => selectHistoryWeek(Number(event.target.value))}
              className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs"
            >
              <option value={0}>Current forecast</option>
              {priorWeekOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <input
            type="date"
            min={summary.window.today}
            max={summary.window.end}
            value={days[Math.min(start, Math.max(0, days.length - 1))]?.date || ""}
            onChange={(event) => {
              const index = days.findIndex((row) => row.date === event.target.value);
              if (index >= 0) setStart(Math.floor(index / 14) * 14);
            }}
            className="h-8 rounded-md border border-gray-200 px-2 text-xs"
          />
          <span className="text-xs text-gray-500">
            {dateLabel(days[Math.min(start, Math.max(0, days.length - 1))]?.date)}
          </span>
          <button
            type="button"
            onClick={() => setStart(Math.max(0, start - 14))}
            className="h-8 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setStart(0)}
            className="h-8 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700"
          >
            Model start
          </button>
          <button
            type="button"
            onClick={() =>
              setStart(Math.min(Math.max(0, days.length - 14), start + 14))
            }
            className="h-8 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700"
          >
            ›
          </button>
        </div>
      </div>
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-[1180px] w-full text-sm">
          <thead className="sticky top-0 z-30 bg-gray-50 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            <tr>
              <th className="sticky left-0 z-40 bg-gray-50 px-3 py-2 text-left whitespace-nowrap min-w-[180px]">
                Daily ledger
              </th>
              {visible.map((row) => (
                <th
                  key={row.date}
                  className={`px-3 py-2 text-right ${isWeekendOrHoliday(row.date, holidayDates) ? "bg-rose-50" : row.state === "mixed" ? "bg-gray-100" : row.state === "forecast" ? "bg-gray-50" : ""}`}
                >
                  {dayLabel(row.date)}
                  <span className="mt-1 block">
                    <Chip tone={row.state}>{row.state}</Chip>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-gray-100 font-semibold">
              <td className="sticky left-0 z-20 bg-gray-50 px-3 py-2 whitespace-nowrap min-w-[180px]">
                Beginning cash
              </td>
              {visible.map((row) => (
                <td
                  key={row.date}
                  className="px-3 py-2 text-right tabular-nums"
                >
                  {money(row.beginningCash)}
                </td>
              ))}
            </tr>
            {categories
              .filter(([key]) => !hiddenCategories.has(key))
              .map(([key, label, tone]) => (
                <tr key={key} className="border-t border-gray-100">
                  <td className="sticky left-0 z-20 bg-white px-3 py-2 font-medium text-gray-700 whitespace-nowrap min-w-[180px]">
                    {label}
                  </td>
                  {visible.map((row) => {
                    const value = row.values?.[key];
                    const override = row.overrides?.[key];
                    const editable = row.date >= summary.window.today;
                    const isEditing =
                      editing?.date === row.date && editing.category === key;
                    return (
                      <td
                        key={row.date}
                        className={`relative px-3 py-2 text-right tabular-nums ${tone} ${row.state === "forecast" ? "bg-gray-50/70" : ""}`}
                      >
                        {isEditing ? (
                          <input
                            autoFocus
                            type="number"
                            defaultValue={value ?? ""}
                            onBlur={(event) =>
                              save(row, key, event.target.value)
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter")
                                save(row, key, event.currentTarget.value);
                              if (event.key === "Escape") setEditing(null);
                            }}
                            className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-sm text-gray-900"
                          />
                        ) : (
                          <div className="group relative inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openDetail(row, key)}
                              onDoubleClick={() =>
                                editable &&
                                setEditing({ date: row.date, category: key })
                              }
                              className={`tabular-nums ${editable ? "hover:underline" : "cursor-default"} ${override && editable ? "border-b border-dashed border-amber-400" : ""}`}
                              title={override?.note || undefined}
                            >
                              {money(value)}
                              {override && editable && (
                                <span className="ml-1 text-amber-500">●</span>
                              )}
                            </button>
                            {override && editable && (
                              <button
                                type="button"
                                onClick={() => clearOverride(row, key)}
                                className="text-xs text-gray-400 hover:text-red-700"
                                title="Clear override"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            <tr className="border-t border-gray-200 font-semibold">
              <td className="sticky left-0 z-20 bg-gray-50 px-3 py-2 whitespace-nowrap min-w-[180px]">
                Ending cash
              </td>
              {visible.map((row) => (
                <td
                  key={row.date}
                  className={`px-3 py-2 text-right tabular-nums ${Number(row.endingCash) < Number(summary.thresholds?.grossCashFloor) ? "text-red-700" : ""}`}
                >
                  {money(row.endingCash)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {selectedCell && selectedRow && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-900">
              {categories.find(([key]) => key === selectedCell.category)?.[1] ||
                selectedCell.category}{" "}
              · {dateLabel(selectedRow.date)} ·{" "}
              {selectedRow.state === "actual" ? "Actual" : "Forecast"} ·{" "}
              {money(selectedRow.values?.[selectedCell.category])}
            </p>
            <button
              type="button"
              onClick={() => setSelectedCell(null)}
              className="text-xs text-gray-500"
            >
              Close
            </button>
          </div>
          <div className="space-y-1 border-b border-gray-100 pb-3 text-xs text-gray-600">
            {(selectedRow.sources?.[selectedCell.category] || []).map(
              (source, index) => (
                <div
                  key={`${source.type}-${source.ref || index}`}
                  className="flex justify-between gap-3"
                >
                  <span>
                    {source.label && (
                      <strong className="mr-1 text-gray-900">
                        {source.label}
                      </strong>
                    )}
                    {sourceLabel(source.type)} · {source.ref || "—"}
                  </span>
                  <span className="tabular-nums">{money(source.amount)}</span>
                </div>
              ),
            )}
            {!selectedRow.sources?.[selectedCell.category]?.length && (
              <p>No source entries.</p>
            )}
          </div>
          {selectedCell.category === "credit_card" &&
            (selectedRow.sources?.credit_card || []).some(
              (source) => source.meta?.cardId,
            ) && (
              <div className="mt-3 space-y-2 border-b border-gray-100 pb-3">
                <p className="text-xs font-semibold text-gray-700">
                  Card-specific overrides
                </p>
                {selectedRow.sources.credit_card
                  .filter((source) => source.meta?.cardId)
                  .map((source) => (
                    <CardOverrideEditor
                      key={source.meta.cardId}
                      row={selectedRow}
                      source={source}
                      onSave={save}
                      onReset={clearOverride}
                    />
                  ))}
              </div>
            )}
          {selectedRow.date >= summary.window.today && (
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              <label className="text-xs text-gray-600">
                Override total (replaces all cards)
                <input
                  type="number"
                  value={detailAmount}
                  onChange={(event) => setDetailAmount(event.target.value)}
                  className="mt-1 h-8 w-full rounded border-gray-200 text-right"
                />
              </label>
              <label className="text-xs text-gray-600">
                Note
                <textarea
                  value={detailNote}
                  onChange={(event) => setDetailNote(event.target.value)}
                  className="mt-1 min-h-8 w-full rounded border-gray-200 text-sm"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveDetail}
                  className="h-8 rounded bg-black px-3 text-xs font-medium text-white"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() =>
                    clearOverride(selectedRow, selectedCell.category)
                  }
                  className="h-8 rounded border border-gray-200 px-3 text-xs text-gray-700"
                >
                  Clear override
                </button>
              </div>
              <div className="rounded border border-gray-100 bg-gray-50 p-2">
                <p className="mb-2 text-xs font-semibold text-gray-700">
                  Add entry
                </p>
                <div className="flex flex-wrap gap-2">
                  <input
                    value={entryForm.label}
                    onChange={(event) =>
                      setEntryForm({ ...entryForm, label: event.target.value })
                    }
                    placeholder="Label"
                    className="h-8 flex-1 rounded border-gray-200 text-xs"
                  />
                  <input
                    type="number"
                    value={entryForm.amount}
                    onChange={(event) =>
                      setEntryForm({ ...entryForm, amount: event.target.value })
                    }
                    placeholder="Amount"
                    className="h-8 w-28 rounded border-gray-200 text-right text-xs"
                  />
                  <button
                    type="button"
                    onClick={addEntry}
                    className="h-8 rounded border border-gray-200 px-3 text-xs text-gray-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
